import type { PlaybackSegment } from '@lucimark/shared/helpers/playbackScheduling'
import type { TPlayerDiagnosticsSnapshot } from '@lucimark/shared/models/player-diagnostics'
import type { ILastCompletedFrameTransition } from '@/helpers/playerDiagnostics/detectFrameTransitionAccelerated'
import {
  getPeakPresentationPlanIdx,
  poisonStalePresentationPeakForE2e,
  restorePlaybackPresentationSessionFromStorage,
  simulatePlaybackPresentationHmrRemountForE2e
} from '@/helpers/playbackPresentationSession'
import { getPlaybackWorkerPayloadSequenceHash } from '@/helpers/queueHash'

export const E2E_PLAYBACK_PROBE_STORAGE_KEY = 'LUCIMARK:E2E_PLAYBACK_PROBE'

export interface E2ePlaybackTransition {
  frameId: string
  notifiedIdx: number
  ts: number
}

export type E2eRegistrationStatusRefetchHandler = () => Promise<void>

export interface E2ePopHeartbeatEntry {
  ts: number
  assetPlays: { assetUrn: string, durationSeconds: number }[]
  playlistUrn: string | null
  popFrameId: string | null
}

export interface E2ePopDiagnostics {
  viewIdx: number
  surfaceFrameIds: string[]
  playingFrameId: string | null
  popFrameId: string | null
}

export type E2eHoldClearReason = 'ready' | 'timeout' | 'supersede' | 'teardown'

export interface E2eHoldDiagnostics {
  active: boolean
  outgoingFrameId: string | null
  targetFrameId: string | null
}

export interface E2eHoldEvent {
  event: 'begin' | 'clear'
  reason?: E2eHoldClearReason
  outgoingFrameId: string | null
  targetFrameId: string | null
  ts: number
}

export interface LucimarkE2ePlaybackProbe {
  getCurrentFrameId: () => string | null
  getNotifiedIdx: () => number
  getQueueFrameIds: () => string[]
  /** Materialized queue dwell seconds in playback order (E2E duration regression). */
  getQueueFrameDurationsSec: () => number[]
  getTransitionLog: () => E2ePlaybackTransition[]
  getWidgetRevealMs: () => number | null
  getRssSlideIndex: () => number | null
  waitForLoopComplete: (timeoutMs?: number) => Promise<boolean>
  /** Re-fetch registration-status through the production adapter path (DYN-* E2E). */
  refetchRegistrationStatus: () => Promise<void>
  /** Ms between consecutive transition log entries for a frame id (dwell timing). */
  getTransitionDwellMs: (frameId: string) => number | null
  /** Proof-of-play heartbeat batches emitted with non-empty `assetPlays` (POP-* E2E). */
  getPopHeartbeatLog: () => E2ePopHeartbeatEntry[]
  /** Buffer vs active PoP frame while video preload may advance `viewIdx` (POP-1 E2E). */
  getPopDiagnostics: () => E2ePopDiagnostics | null
  /**
   * Force a coalesced PoP flush (P2). Used by POP-* E2E instead of waiting for the 60s cadence.
   */
  flushProofOfPlay: () => void
  /** Sticky hold-until-ready snapshot (HOLD-* E2E). */
  getHoldDiagnostics: () => E2eHoldDiagnostics | null
  /** Sticky hold begin/clear event log (HOLD-* E2E). */
  getHoldEventLog: () => E2eHoldEvent[]
  /** Simulates Vite HMR clearing globalThis then restoring from sessionStorage (RUN-2). */
  simulateVBufferHmrRemount: () => void
  /** Player diagnostics snapshot when kill switch / session is enabled (RUN-3). */
  getDiagnosticsSnapshot: () => TPlayerDiagnosticsSnapshot | null
  /** Inject a completed frame transition for accelerated-fault E2E (RUN-3). */
  injectCompletedFrameTransition: (transition: ILastCompletedFrameTransition) => void
  /** Monotonic presentation peak (authority) — DYN-3 / #1662. */
  getPeakPresentationPlanIdx: () => number
  /**
   * Leave peak/logged/notified at an out-of-range index after a rematch shrink (#1662).
   * Production leaves this behind; rematch E2E often force-sets, so tests re-poison.
   */
  poisonStalePresentationPeak: (peakIdx: number) => void
}

export type E2eCompletedFrameTransitionInjector = (transition: ILastCompletedFrameTransition) => void

export type E2eDiagnosticsSnapshotProvider = () => TPlayerDiagnosticsSnapshot | null

export type E2ePopFlushHandler = () => void

let transitionLog: E2ePlaybackTransition[] = []
let currentFrameId: string | null = null
let notifiedIdx = -1
let queueFrameIds: string[] = []
let queueFrameDurationsSec: number[] = []
let widgetRevealMs: number | null = null
let rssSlideIndex: number | null = null
let loopCompleteResolver: (() => void) | null = null
let loopCompletePromise: Promise<boolean> | null = null
let registrationStatusRefetchHandler: E2eRegistrationStatusRefetchHandler | null = null
let popHeartbeatLog: E2ePopHeartbeatEntry[] = []
let popDiagnostics: E2ePopDiagnostics | null = null
let holdEventLog: E2eHoldEvent[] = []
let holdDiagnostics: E2eHoldDiagnostics | null = null
let completedFrameTransitionInjector: E2eCompletedFrameTransitionInjector | null = null
let diagnosticsSnapshotProvider: E2eDiagnosticsSnapshotProvider | null = null
let popFlushHandler: E2ePopFlushHandler | null = null

export function registerE2eCompletedFrameTransitionInjector(
  handler: E2eCompletedFrameTransitionInjector | null
): void {
  completedFrameTransitionInjector = handler
}

export function registerE2eDiagnosticsSnapshotProvider(
  provider: E2eDiagnosticsSnapshotProvider | null
): void {
  diagnosticsSnapshotProvider = provider
}

export function registerE2ePopFlushHandler(handler: E2ePopFlushHandler | null): void {
  popFlushHandler = handler
}

export function isE2ePlaybackProbeEnabled(): boolean {
  try {
    if (localStorage.getItem(E2E_PLAYBACK_PROBE_STORAGE_KEY) !== 'true') {
      return false
    }
  }
  catch {
    return false
  }
  // Dev: localStorage alone. Production/staging Web OTA: also require ?e2eProbe=1
  // so Slimbox canary can opt in without enabling the probe fleet-wide.
  if (import.meta.env.DEV) {
    return true
  }
  try {
    return new URLSearchParams(window.location.search).get('e2eProbe') === '1'
  }
  catch {
    return false
  }
}

export function registerE2eRegistrationStatusRefetch(handler: E2eRegistrationStatusRefetchHandler | null): void {
  registrationStatusRefetchHandler = handler
}

export function resetE2ePlaybackProbeState(): void {
  transitionLog = []
  currentFrameId = null
  notifiedIdx = -1
  queueFrameIds = []
  queueFrameDurationsSec = []
  widgetRevealMs = null
  rssSlideIndex = null
  loopCompleteResolver = null
  loopCompletePromise = null
  popHeartbeatLog = []
  popDiagnostics = null
  holdEventLog = []
  holdDiagnostics = null
}

export function recordE2ePopHeartbeat(entry: E2ePopHeartbeatEntry): void {
  if (!isE2ePlaybackProbeEnabled()) {
    return
  }
  popHeartbeatLog.push(entry)
}

export function setE2ePopDiagnostics(diagnostics: E2ePopDiagnostics): void {
  if (!isE2ePlaybackProbeEnabled()) {
    return
  }
  popDiagnostics = diagnostics
}

export function setE2eHoldDiagnostics(diagnostics: E2eHoldDiagnostics): void {
  if (!isE2ePlaybackProbeEnabled()) {
    return
  }
  holdDiagnostics = diagnostics
}

export function recordE2eHoldEvent(entry: E2eHoldEvent): void {
  if (!isE2ePlaybackProbeEnabled()) {
    return
  }
  holdEventLog.push(entry)
}

export function setE2eQueueFrameIds(ids: string[], durationsSec: readonly number[] = []): void {
  if (!isE2ePlaybackProbeEnabled()) {
    return
  }
  queueFrameIds = [...ids]
  queueFrameDurationsSec = [...durationsSec]
}

export function setE2eWidgetRevealMs(ms: number | null): void {
  if (!isE2ePlaybackProbeEnabled()) {
    return
  }
  widgetRevealMs = ms
}

export function setE2eRssSlideIndex(index: number | null): void {
  if (!isE2ePlaybackProbeEnabled()) {
    return
  }
  rssSlideIndex = index
}

export function recordE2ePresentationCommit(
  segments: PlaybackSegment[],
  nextNotifiedIdx: number,
  opts?: { loopWrapped?: boolean }
): void {
  if (!isE2ePlaybackProbeEnabled()) {
    return
  }
  const onAir = segments[0]
  const prevFrameId = currentFrameId
  if (onAir?.frameId) {
    currentFrameId = onAir.frameId
  }
  // Len-1 rotating embeds keep notifiedIdx at 0 — still log when the creative changes (#1382).
  if (
    nextNotifiedIdx >= 0
    && (nextNotifiedIdx !== notifiedIdx || (onAir?.frameId != null && onAir.frameId !== prevFrameId))
  ) {
    notifiedIdx = nextNotifiedIdx
    transitionLog.push({
      frameId: onAir?.frameId ?? '',
      notifiedIdx: nextNotifiedIdx,
      ts: performance.now()
    })
  }
  if (opts?.loopWrapped === true && loopCompleteResolver) {
    loopCompleteResolver()
    loopCompleteResolver = null
  }
}

function getTransitionDwellMs(frameId: string): number | null {
  const entries = transitionLog.filter(e => e.frameId === frameId)
  if (entries.length < 2) {
    return null
  }
  const first = entries[0]!
  const second = entries[1]!
  return second.ts - first.ts
}

function buildProbeApi(): LucimarkE2ePlaybackProbe {
  return {
    getCurrentFrameId: () => currentFrameId,
    getNotifiedIdx: () => notifiedIdx,
    getQueueFrameIds: () => [...queueFrameIds],
    getQueueFrameDurationsSec: () => [...queueFrameDurationsSec],
    getTransitionLog: () => [...transitionLog],
    getWidgetRevealMs: () => widgetRevealMs,
    getRssSlideIndex: () => rssSlideIndex,
    waitForLoopComplete: (timeoutMs = 60_000) => {
      if (loopCompletePromise) {
        return loopCompletePromise
      }
      loopCompletePromise = new Promise<boolean>((resolve) => {
        const timer = window.setTimeout(() => {
          loopCompleteResolver = null
          loopCompletePromise = null
          resolve(false)
        }, timeoutMs)
        loopCompleteResolver = () => {
          window.clearTimeout(timer)
          loopCompletePromise = null
          resolve(true)
        }
      })
      return loopCompletePromise
    },
    refetchRegistrationStatus: async () => {
      if (registrationStatusRefetchHandler == null) {
        throw new Error('E2E registration refetch handler not registered')
      }
      await registrationStatusRefetchHandler()
    },
    getTransitionDwellMs,
    getPopHeartbeatLog: () => [...popHeartbeatLog],
    getPopDiagnostics: () => (popDiagnostics != null ? { ...popDiagnostics, surfaceFrameIds: [...popDiagnostics.surfaceFrameIds] } : null),
    flushProofOfPlay: () => {
      if (popFlushHandler == null) {
        throw new Error('E2E proof-of-play flush handler not registered')
      }
      popFlushHandler()
    },
    getHoldDiagnostics: () => (holdDiagnostics != null ? { ...holdDiagnostics } : null),
    getHoldEventLog: () => [...holdEventLog],
    simulateVBufferHmrRemount: () => {
      simulatePlaybackPresentationHmrRemountForE2e()
      let spineHash = ''
      try {
        spineHash = sessionStorage.getItem('lucimark.playbackPlanSpineHash') ?? ''
      }
      catch {
        // ignore
      }
      if (spineHash === '' && queueFrameIds.length > 0) {
        const plan: PlaybackSegment[] = queueFrameIds.map((frameId, i) => ({
          frameId,
          durationSeconds: queueFrameDurationsSec[i] ?? 3,
          playlistUrn: 'urn:lucimark:e2e:probe-spine',
          frameIndex: i,
          playlistSeqNo: i
        }))
        spineHash = getPlaybackWorkerPayloadSequenceHash(plan)
      }
      restorePlaybackPresentationSessionFromStorage(spineHash)
    },
    getDiagnosticsSnapshot: () => diagnosticsSnapshotProvider?.() ?? null,
    injectCompletedFrameTransition: (transition) => {
      if (completedFrameTransitionInjector == null) {
        throw new Error('E2E completed frame transition injector not registered')
      }
      completedFrameTransitionInjector(transition)
    },
    getPeakPresentationPlanIdx: () => getPeakPresentationPlanIdx(),
    poisonStalePresentationPeak: (peakIdx) => {
      poisonStalePresentationPeakForE2e(peakIdx)
    }
  }
}

export function installE2ePlaybackProbe(): void {
  if (!isE2ePlaybackProbeEnabled()) {
    return
  }
  window.__LUCIMARK_E2E__ = buildProbeApi()
}

export function notifyE2eLoopWrap(): void {
  recordE2ePresentationCommit([], notifiedIdx, { loopWrapped: true })
}
